import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Autocomplete,
  Divider
} from '@mui/material';
import { useSnackbar } from 'notistack';
import estrategiaService, {
  Estrategia,
  ESTRATEGIA_IMPUTABLE_FIELDS,
  ESTRATEGIA_IMPUTABLE_SUBTABS,
  EstrategiaImputableFieldKey,
  normalizeEstrategiaFromApi
} from '../../services/estrategiaService';
import imputableService from '../../services/imputableService';
import type { Imputable } from '../../services/imputableService';
import SearchableSelect, { searchableSelectAutocompleteProps } from '../../components/SearchableSelect';

const tiposEstrategia = [
  { value: 'C', label: 'Compras' },
  { value: 'V', label: 'Ventas' },
  { value: 'T', label: 'Tesorería' },
  { value: 'O', label: 'Otros' }
];

const imputableDefaults = Object.fromEntries(
  ESTRATEGIA_IMPUTABLE_FIELDS.map(({ key }) => [key, null])
) as Record<EstrategiaImputableFieldKey, number | null>;

const defaultForm: Estrategia = {
  nint: 0,
  nombre: '',
  tipo: 'C',
  activo: 'S',
  descripcion: '',
  ...imputableDefaults
};

const ESTRATEGIA_FIELD_LABEL = Object.fromEntries(
  ESTRATEGIA_IMPUTABLE_FIELDS.map(({ key, label }) => [key, label])
) as Record<EstrategiaImputableFieldKey, string>;

const ImputableAutocomplete: React.FC<{
  label: string;
  nroCuenta: number | null | undefined;
  nomDisplay: string;
  onPick: (nro: number | null, nom: string) => void;
}> = ({ label, nroCuenta, nomDisplay, onPick }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<Imputable[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const q = input.trim() || (nroCuenta != null && nroCuenta !== 0 ? String(nroCuenta) : '');
        const res = await imputableService.getImputablesSearchPaged({
          pageNumber: 1,
          pageSize: 20,
          searchTerm: q
        });
        const raw = res.items as unknown as Record<string, unknown>[];
        setOptions(
          raw.map((item) => ({
            nroCuenta: Number(item.nroCuenta ?? item.NroCuenta ?? 0),
            nomCuenta: String(item.nomCuenta ?? item.NomCuenta ?? '')
          }))
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [input, nroCuenta]);

  const value: Imputable | null = useMemo(() => {
    if (nroCuenta == null || nroCuenta === 0) return null;
    return {
      nroCuenta: Number(nroCuenta),
      nomCuenta: nomDisplay || String(nroCuenta)
    };
  }, [nroCuenta, nomDisplay]);

  return (
    <Autocomplete
      {...searchableSelectAutocompleteProps}
      loading={loading}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (o?.nroCuenta != null ? `${o.nroCuenta} — ${o.nomCuenta ?? ''}` : '')}
      isOptionEqualToValue={(a, b) => a?.nroCuenta === b?.nroCuenta}
      value={value}
      onChange={(_, v) => {
        if (v?.nroCuenta != null) onPick(v.nroCuenta, v.nomCuenta ?? '');
        else onPick(null, '');
      }}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      renderInput={(params) => <TextField {...params} label={label} placeholder="Buscar cuenta…" size="small" />}
      sx={{ width: '100%' }}
    />
  );
};

const EstrategiaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nint } = useParams();
  const [formData, setFormData] = useState<Estrategia>({ ...defaultForm });
  const [cuentaNombres, setCuentaNombres] = useState<Partial<Record<EstrategiaImputableFieldKey, string>>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [moduloSubTab, setModuloSubTab] = useState(0);

  const isNew = !nint || nint === 'nuevo';

  useEffect(() => {
    const load = async () => {
      if (!nint || nint === 'nuevo') {
        setFormData({ ...defaultForm });
        setCuentaNombres({});
        return;
      }
      try {
        setLoading(true);
        const raw = await estrategiaService.getEstrategia(Number(nint));
        const normalized = normalizeEstrategiaFromApi(raw);
        setFormData({ ...defaultForm, ...normalized } as Estrategia);

        const labels: Partial<Record<EstrategiaImputableFieldKey, string>> = {};
        await Promise.all(
          ESTRATEGIA_IMPUTABLE_FIELDS.map(async ({ key }) => {
            const nro = normalized[key];
            if (nro == null || !Number.isFinite(Number(nro))) return;
            const imp = await imputableService.getImputable(Number(nro));
            if (imp?.nomCuenta) labels[key] = imp.nomCuenta;
          })
        );
        setCuentaNombres(labels);
      } catch {
        enqueueSnackbar('Error al cargar la estrategia', { variant: 'error' });
        navigate('/contabilidad/estrategia');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [nint, enqueueSnackbar, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nint') {
      setFormData((prev) => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSwitchChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.checked ? 'S' : 'N' }));
  };

  const handleImputablePick =
    (field: EstrategiaImputableFieldKey) => (nro: number | null, nom: string) => {
      setFormData((prev) => ({ ...prev, [field]: nro }));
      setCuentaNombres((prev) => ({ ...prev, [field]: nom }));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);

      if (isNew && !formData.nombre.trim()) {
        enqueueSnackbar('El nombre de la estrategia es requerido', { variant: 'warning' });
        return;
      }

      if (isNew) {
        await estrategiaService.createEstrategia(formData);
      } else {
        await estrategiaService.updateEstrategia(Number(nint), formData);
      }

      enqueueSnackbar('Estrategia guardada exitosamente', { variant: 'success' });
      navigate('/contabilidad/estrategia');
    } catch (error: unknown) {
      console.error('Error al guardar estrategia:', error);
      const err = error as { response?: { data?: { message?: string } } };
      enqueueSnackbar(err?.response?.data?.message || 'Error al guardar la estrategia', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const activeModuloKeys = ESTRATEGIA_IMPUTABLE_SUBTABS[moduloSubTab]?.keys ?? [];

  if (loading) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#f5f5f5',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
            {isNew ? 'Nueva Estrategia' : 'Editar Estrategia'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  name="nint"
                  label="N° Interno"
                  type="number"
                  value={formData.nint}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={!isNew}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="nombre"
                  label="Nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  fullWidth
                  required={isNew}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  name="tipo"
                  label="Tipo"
                  size="small"
                  value={formData.tipo || null}
                  onChange={(v) => setFormData((prev) => ({ ...prev, tipo: v ?? '' }))}
                  options={tiposEstrategia}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={<Switch checked={formData.activo === 'S'} onChange={handleSwitchChange('activo')} />}
                  label="Activo"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="descripcion"
                  label="Descripción"
                  value={formData.descripcion || ''}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
              Cuentas imputables por módulo
            </Typography>
            <Tabs
              value={moduloSubTab}
              onChange={(_, v) => setModuloSubTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {ESTRATEGIA_IMPUTABLE_SUBTABS.map((st) => (
                <Tab key={st.modulo} label={st.label} />
              ))}
            </Tabs>

            <Grid container spacing={2}>
              {activeModuloKeys.map((key) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <ImputableAutocomplete
                    label={ESTRATEGIA_FIELD_LABEL[key]}
                    nroCuenta={formData[key]}
                    nomDisplay={cuentaNombres[key] ?? ''}
                    onPick={handleImputablePick(key)}
                  />
                </Grid>
              ))}
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/contabilidad/estrategia')}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EstrategiaForm;
